import { useCallback, useEffect, useState } from 'react';

import { EmployeeListInMapTab } from 'bringg-web/services/cross-application/cross-application.actions';
import { useStores } from 'bringg-web/recipes';
import { executeAction, useCrossApp } from 'bringg-web/services/cross-application/cross-application';
import { EmployeeAvailabilityState, EmployeeStateFilter } from 'bringg-web/features/dispatch-page/employee-list/types';
import type { RegisterCallbacks, DisplayedEmployees } from '../employee-list';

const DEFAULT_DISPLAYED_EMPLOYEES: DisplayedEmployees = {
	all: [],
	filtered: [],
	onlineCount: 0,
	onShiftAndOfflineCount: 0,
	onShiftCount: 0,
	offShiftCount: 0
};

export const useCrossAppData = () => {
	const { authStore } = useStores();
	const { crossAppTransport } = authStore;

	const [initFinished, setInitFinished] = useState(false);
	const [driverListFilterByOnShift, setDriverListFilterByOnShift] = useState<() => boolean>(() => () => false);
	const [selectedEmployee, setSelectedEmployee] = useState<Bringg.User | null>(null);
	const [displayedEmployees, setDisplayedEmployees] = useState<DisplayedEmployees>(DEFAULT_DISPLAYED_EMPLOYEES);

	const setDisplayedEmployeesWithFallbackToDefault = useCallback(
		(newDisplayedEmployees: DisplayedEmployees) => {
			setDisplayedEmployees(newDisplayedEmployees || DEFAULT_DISPLAYED_EMPLOYEES);
		},
		[setDisplayedEmployees]
	);

	useCrossApp(
		EmployeeListInMapTab.EMPLOYEE_LIST_EMPLOYEES_UPDATED,
		setDisplayedEmployeesWithFallbackToDefault,
		authStore.crossAppTransport
	);

	useCrossApp(
		EmployeeListInMapTab.EMPLOYEE_LIST_SELECTED_EMPLOYEE_UPDATED,
		setSelectedEmployee,
		authStore.crossAppTransport
	);

	function filterEmployees(employeeNameQuery: string) {
		executeAction(crossAppTransport, EmployeeListInMapTab.EMPLOYEE_FILTER_CHANGE, { query: employeeNameQuery });
	}

	function changeEmployeesFilterType(filterType: EmployeeStateFilter) {
		executeAction(crossAppTransport, EmployeeListInMapTab.CHANGE_EMPLOYEE_STATE_FILTER, filterType);
	}

	function changeEmployeesAvailabilityStateFilter(filterType: EmployeeAvailabilityState | undefined) {
		executeAction(crossAppTransport, EmployeeListInMapTab.CHANGE_EMPLOYEE_AVAILABILITY_STATE_FILTER, filterType);
	}

	useEffect(() => {
		// eslint-disable-next-line prefer-const
		let interval: number;

		const initCbs: RegisterCallbacks = {
			setSelectedEmployee,
			setDisplayedEmployees(newDisplayed: DisplayedEmployees) {
				setDisplayedEmployees(newDisplayed ? newDisplayed : DEFAULT_DISPLAYED_EMPLOYEES);
			},
			setDriverListFilterByOnShift(fn: () => boolean) {
				return setDriverListFilterByOnShift(() => fn);
			},
			// Must be called after all above functions called
			finish() {
				clearInterval(interval);
				setInitFinished(true);
			}
		};

		interval = setInterval(() => {
			executeAction(crossAppTransport, EmployeeListInMapTab.EMPLOYEE_LIST_REGISTER, initCbs);
		}, 100) as unknown as number;

		executeAction(crossAppTransport, EmployeeListInMapTab.EMPLOYEE_LIST_REGISTER, initCbs);

		return () => clearInterval(interval);
	}, [crossAppTransport]);

	return {
		initFinished,
		displayedEmployees,
		selectedEmployee,
		filterEmployees,
		changeEmployeesFilterType,
		changeEmployeesAvailabilityStateFilter,
		driverListFilterByOnShift
	};
};
